import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, addHours, isToday, parse, isBefore, startOfDay } from 'date-fns';
import { CalendarCheck, CalendarIcon, Clock, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useSubmitInquiry } from '@/hooks/useInquiries';
import { Property } from '@/types/property';
import { formatPrice } from '@/lib/formatters';
import { toast } from '@/hooks/use-toast';


const ALL_TIME_SLOTS = [
  '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
  '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM',
  '6:00 PM', '7:00 PM', '8:00 PM'
];

// Minimum hours in advance for booking (to give seller time to prepare)
const MIN_HOURS_ADVANCE = 3;

// Anti-spam: limit viewing requests per browser session
const MAX_REQUESTS_PER_SESSION = 3;
const SESSION_STORAGE_KEY = 'viewing_request_count';

const getSessionRequestCount = (): number => {
  try {
    return parseInt(sessionStorage.getItem(SESSION_STORAGE_KEY) || '0', 10);
  } catch {
    return 0;
  }
};

const incrementSessionRequestCount = (): void => {
  try {
    const current = getSessionRequestCount();
    sessionStorage.setItem(SESSION_STORAGE_KEY, String(current + 1));
  } catch {
    // sessionStorage unavailable — fail open
  }
};

// Regex for Canadian/US phone: accepts formats like (555) 123-4567, 555-123-4567, 5551234567, +1 555 123 4567
const phoneRegex = /^(\+1[-.\s]?)?(\(?\d{3}\)?[-.\s]?)?\d{3}[-.\s]?\d{4}$/;

// Auto-format phone number as (XXX) XXX-XXXX
const formatPhoneNumber = (value: string): string => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Handle +1 prefix
  const hasCountryCode = digits.length > 10;
  const phoneDigits = hasCountryCode ? digits.slice(-10) : digits;
  
  // Format based on length
  if (phoneDigits.length <= 3) {
    return phoneDigits;
  } else if (phoneDigits.length <= 6) {
    return `(${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3)}`;
  } else {
    return `(${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6, 10)}`;
  }
};

const inquirySchema = z.object({
  name: z.string().trim().min(2, 'Full name is required'),
  email: z.string().trim().email('Valid email is required'),
  phone: z.string().trim()
    .min(1, 'Phone number is required')
    .regex(phoneRegex, 'Please enter a valid phone number (e.g., 555-123-4567)'),
  preferredDate: z.date({ required_error: 'Please select a preferred date' }),
  preferredTime: z.string({ required_error: 'Please select a preferred time' }),
  message: z.string().min(10, 'Message must be at least 10 characters'),
});

type InquiryFormData = z.infer<typeof inquirySchema>;

interface ContactFormProps {
  property: Property;
}

export function ContactForm({ property }: ContactFormProps) {
  const { user } = useAuth();
  const submitInquiry = useSubmitInquiry();
  const [submitted, setSubmitted] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);

  const defaultMessage = `Hi, I'd like to book a viewing for the property at ${property.address}. Please confirm my preferred time via email.`;

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<InquiryFormData>({
    resolver: zodResolver(inquirySchema),
    defaultValues: {
      message: defaultMessage,
    },
  });

  const selectedDate = watch('preferredDate');
  const selectedTime = watch('preferredTime');

  // Calculate which time slots are available based on selected date
  // If today, times must be at least 3 hours from now
  const timeSlotStatus = useMemo(() => {
    if (!selectedDate) {
      return ALL_TIME_SLOTS.map(slot => ({ slot, available: true, reason: null }));
    }
    
    if (!isToday(selectedDate)) {
      return ALL_TIME_SLOTS.map(slot => ({ slot, available: true, reason: null }));
    }
    
    const minTime = addHours(new Date(), MIN_HOURS_ADVANCE);
    
    return ALL_TIME_SLOTS.map((slot) => {
      const slotTime = parse(slot, 'h:mm a', selectedDate);
      const available = !isBefore(slotTime, minTime);
      return { 
        slot, 
        available, 
        reason: available ? null : 'Less than 3 hours from now' 
      };
    });
  }, [selectedDate]);
  
  const hasAvailableSlots = timeSlotStatus.some(s => s.available);

  const onSubmit = async (data: InquiryFormData) => {
    // Anti-spam: enforce per-session request limit
    if (getSessionRequestCount() >= MAX_REQUESTS_PER_SESSION) {
      toast({
        title: "Too many requests",
        description: `You've reached the limit of ${MAX_REQUESTS_PER_SESSION} viewing requests this session. Please email info@bookviewing.ca if you need to book more.`,
        variant: "destructive",
      });
      return;
    }

    await submitInquiry.mutateAsync({
      propertyId: property.id,
      name: data.name,
      email: data.email,
      phone: data.phone,
      preferredDate: format(data.preferredDate, 'yyyy-MM-dd'),
      preferredTime: data.preferredTime,
      message: data.message,
      propertyTitle: property.title,
      propertyAddress: property.address,
    });

    incrementSessionRequestCount();

    // Track Google Ads conversion
    if (typeof window !== 'undefined' && 'gtag' in window) {
      (window as Window & { gtag: (...args: unknown[]) => void }).gtag('event', 'conversion', {
        'send_to': 'AW-16451038844/FS8SCK2J2uAbEPzcu6Q9',
        'value': property.price,
        'currency': 'CAD',
      });
    }

    setSubmitted(true);
    toast({
      title: "Viewing Request Sent!",
      description: "You'll receive a confirmation email shortly with viewing details.",
    });
  };

  if (submitted) {
    return (
      <Card className="border-primary/30 overflow-hidden">
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-6 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center animate-in zoom-in-50 duration-300">
            <CalendarCheck className="w-8 h-8 text-primary" />
          </div>
          <h3 className="font-semibold text-xl mb-2">Your Showing is Confirmed! 🎉</h3>
          <p className="text-muted-foreground">
            See you there! If there's any change, we'll let you know.
          </p>
        </div>
        <CardContent className="p-6 space-y-4">
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <CalendarIcon className="w-4 h-4 text-primary" />
              <span className="font-medium">Date:</span>
              <span>{selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : 'Flexible'}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-primary" />
              <span className="font-medium">Time:</span>
              <span>{selectedTime || 'Flexible'}</span>
            </div>
          </div>
          <div className="text-sm text-muted-foreground space-y-2">
            <p>
              📧 Check your inbox for a confirmation email with all the details.
            </p>
            <p className="text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 rounded-md p-3 text-xs">
              Don't see our email? Check your spam or junk folder!
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="sticky top-20 border-primary/20">
      <CardHeader className="bg-primary/5 rounded-t-lg">
        <CardTitle className="flex items-center gap-2">
          <CalendarCheck className="w-5 h-5 text-primary" />
          Book a Viewing
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Schedule a tour of this {formatPrice(property.price)} property
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder="Your full name"
              {...register('name')}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              {...register('email')}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone <span className="text-destructive">*</span></Label>
            <Input
              id="phone"
              type="tel"
              placeholder="(555) 123-4567"
              value={watch('phone') || ''}
              onChange={(e) => {
                const formatted = formatPhoneNumber(e.target.value);
                setValue('phone', formatted, { shouldValidate: true });
              }}
            />
            {errors.phone && (
              <p className="text-sm text-destructive">{errors.phone.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Preferred Date</Label>
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !selectedDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, "MMM d, yyyy") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => {
                      if (date) {
                        setValue('preferredDate', date);
                        setDateOpen(false);
                      }
                    }}
                    disabled={(date) => date < startOfDay(new Date())}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              {errors.preferredDate && (
                <p className="text-sm text-destructive">{errors.preferredDate.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Preferred Time</Label>
              <Select 
                onValueChange={(value) => setValue('preferredTime', value)} 
                value={selectedTime}
                disabled={!selectedDate}
              >
                <SelectTrigger className={cn(!selectedTime && "text-muted-foreground")}>
                  <Clock className="mr-2 h-4 w-4" />
                  <SelectValue placeholder={selectedDate ? "Select time" : "Select date first"} />
                </SelectTrigger>
                <SelectContent>
                  {hasAvailableSlots ? (
                    timeSlotStatus.map(({ slot, available, reason }) => (
                      <SelectItem 
                        key={slot} 
                        value={slot}
                        disabled={!available}
                        className={!available ? "opacity-50 line-through" : ""}
                      >
                        <span className="flex items-center gap-2">
                          {slot}
                          {!available && (
                            <span className="text-xs text-muted-foreground font-normal">
                              (too soon)
                            </span>
                          )}
                        </span>
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                      No times available today.<br />Please select tomorrow.
                    </div>
                  )}
                </SelectContent>
              </Select>
              {errors.preferredTime && (
                <p className="text-sm text-destructive">{errors.preferredTime.message}</p>
              )}
            </div>
          </div>
          
          {/* Helpful tip about booking times */}
          <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg text-xs text-muted-foreground">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>
              <strong>Tip:</strong> Please book at least 3 hours in advance so the seller has time to prepare for your visit.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              rows={3}
              placeholder="I'd like to schedule a viewing..."
              {...register('message')}
            />
            {errors.message && (
              <p className="text-sm text-destructive">{errors.message.message}</p>
            )}
          </div>

          <Button 
            type="submit" 
            className="w-full relative" 
            size="lg"
            disabled={submitInquiry.isPending}
          >
            {submitInquiry.isPending ? (
              <>
                <span className="opacity-0">Book a Viewing</span>
                <span className="absolute inset-0 flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  Sending...
                </span>
              </>
            ) : (
              <>
                <CalendarCheck className="w-4 h-4 mr-2" />
                Book a Viewing
              </>
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            By submitting, you agree to receive communications about this property.
          </p>

        </form>
      </CardContent>
    </Card>
  );
}
